import {
  ImmomioAnchorComponent,
  ImmomioButtonComponent
} from '@immomio/ui/button';
import {
  Component,
  computed,
  effect,
  inject,
  input,
  OnInit,
  untracked,
  ChangeDetectionStrategy
} from '@angular/core';
import {
  FormBuilder,
  FormsModule,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslatePipe } from '@ngx-translate/core';
import {
  LogoComponent,
  ModalContentComponent,
  ModalFooterComponent,
  ModalV2Component,
  PasswordComponent,
  passwordValidators
} from '@ui/legacy-lib';
import { CaptchaSettings } from '@ui/shared/model';
import {
  externalDataPrivacyLink,
  termsLink
} from '@ui/tenant-pool/shared/config/util';
import { FriendlyCaptchaComponent } from '@ui/tenant-pool/shared/friendly-captcha/ui';

@Component({
  selector: 'app-guest-register-modal',
  templateUrl: './guest-register-modal.component.html',
  styleUrls: ['./guest-register-modal.component.scss'],

  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ImmomioButtonComponent,
    ImmomioAnchorComponent,
    FormsModule,
    ReactiveFormsModule,
    TranslatePipe,
    ModalV2Component,
    ModalContentComponent,
    LogoComponent,
    PasswordComponent,
    ModalFooterComponent,
    FriendlyCaptchaComponent
  ]
})
export class GuestRegisterModalComponent implements OnInit {
  private ngbActiveModal = inject(NgbActiveModal);
  private formBuilder = inject(FormBuilder);
  protected readonly termsUrl = termsLink.url;
  protected readonly externalDataPrivacyLink = externalDataPrivacyLink;

  public email = input<string>(undefined);
  public declareIntent = input<boolean>(false);
  public isApplying = input<boolean>(false);
  public itpCustomerName = input<string>(undefined);
  public captchaSettings = input<CaptchaSettings>(undefined);

  public readonly captchaSiteKey = computed(
    () => this.captchaSettings()?.siteKey ?? ''
  );

  /**
   * In the apply flow the captcha is already collected on the review step and
   * sent with the application, so we skip it here to avoid double-prompting.
   */
  public readonly captchaActive = computed(
    () =>
      !this.isApplying() &&
      this.captchaSettings()?.applicationSubmissionActive === true &&
      !!this.captchaSiteKey()
  );

  public form = this.formBuilder.group({
    email: [''],
    password: ['', passwordValidators()],
    captcha: ['']
  });

  constructor() {
    effect(() => {
      const captchaActive = this.captchaActive();

      untracked(() => {
        const captchaControl = this.form.get('captcha');
        if (captchaActive) {
          captchaControl?.setValidators(Validators.required);
        } else {
          captchaControl?.clearValidators();
        }
        captchaControl?.updateValueAndValidity({ emitEvent: false });
      });
    });
  }

  ngOnInit() {
    this.form.patchValue({
      email: this.email()
    });
  }

  public dismiss() {
    this.ngbActiveModal.dismiss();
  }

  public continueAsGuest() {
    this.ngbActiveModal.close();
  }

  public register() {
    if (this.form.invalid) return;
    this.ngbActiveModal.close(this.form.value);
  }
}
